import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../config/api';
import sttService from '../services/sttService';
import { isPublicProductionHost } from '../utils/publicHost';

const AuthContext = createContext(null);
const AUTH_KEY = 'niva_auth';
const NURSE_NAME_KEY = 'niva_nurse_name';
const JWT_KEY = 'niva_jwt';
const MCP_KEY = 'niva_must_change_password';
const TERMS_KEY = 'niva_terms_accepted';
const ACCOUNT_TYPE_KEY = 'niva_account_type';

function loadNurseName() {
  try {
    return localStorage.getItem(NURSE_NAME_KEY) || '';
  } catch {
    return '';
  }
}

function loadAccountType() {
  try {
    return localStorage.getItem(ACCOUNT_TYPE_KEY) || 'hospital';
  } catch {
    return 'hospital';
  }
}

function readFlag(key) {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeFlag(key, value) {
  try {
    localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // ignore
  }
}

function clearDoctorSessionIfProduction() {
  if (!isPublicProductionHost()) return;
  try {
    if (localStorage.getItem(ACCOUNT_TYPE_KEY) === 'doctor') {
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem(JWT_KEY);
      localStorage.removeItem(MCP_KEY);
      localStorage.removeItem(ACCOUNT_TYPE_KEY);
    }
  } catch {
    // ignore
  }
}

clearDoctorSessionIfProduction();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return localStorage.getItem(AUTH_KEY) === 'true' || Boolean(localStorage.getItem(JWT_KEY));
    } catch {
      return false;
    }
  });
  const [nurseName, setNurseName] = useState(loadNurseName);
  const [accountType, setAccountType] = useState(loadAccountType);
  const [mustChangePassword, setMustChangePassword] = useState(() => readFlag(MCP_KEY));
  const [needsConsent, setNeedsConsent] = useState(() => {
    try {
      if (!(localStorage.getItem(AUTH_KEY) === 'true' || localStorage.getItem(JWT_KEY))) {
        return false;
      }
      if (loadAccountType() === 'doctor') return false;
      return localStorage.getItem(TERMS_KEY) !== 'true';
    } catch {
      return false;
    }
  });
  const [consentStatusReady, setConsentStatusReady] = useState(() => !isAuthenticated);

  const persistAuth = (name, jwt, mustChange, termsAccepted, nextAccountType = 'hospital') => {
    try {
      localStorage.setItem(AUTH_KEY, 'true');
      if (name) localStorage.setItem(NURSE_NAME_KEY, name);
      if (jwt) localStorage.setItem(JWT_KEY, jwt);
      localStorage.setItem(ACCOUNT_TYPE_KEY, nextAccountType);
      writeFlag(MCP_KEY, mustChange);
      writeFlag(TERMS_KEY, termsAccepted);
    } catch {
      // ignore
    }
  };

  const applyConsentAccepted = useCallback((accepted) => {
    const value = Boolean(accepted);
    setNeedsConsent(!value);
    writeFlag(TERMS_KEY, value);
  }, []);

  const loginWithToken = useCallback((jwt, name, options = {}) => {
    const mustChange = Boolean(options.mustChangePassword);
    const nextAccountType = options.accountType || 'hospital';
    const termsAccepted =
      nextAccountType === 'doctor' ? true : Boolean(options.termsAccepted);
    setNurseName(name || '');
    setAccountType(nextAccountType);
    setIsAuthenticated(true);
    setMustChangePassword(mustChange);
    setNeedsConsent(nextAccountType === 'doctor' ? false : !termsAccepted);
    setConsentStatusReady(true);
    persistAuth(name, jwt, mustChange, termsAccepted, nextAccountType);
    return {
      ok: true,
      mustChangePassword: mustChange,
      needsConsent: nextAccountType === 'doctor' ? false : !termsAccepted,
      accountType: nextAccountType,
    };
  }, []);

  const login = useCallback((name) => {
    const trimmed = name?.trim();
    if (!trimmed) {
      return { ok: false, error: 'enterName' };
    }
    setNurseName(trimmed);
    setAccountType('hospital');
    setIsAuthenticated(true);
    setMustChangePassword(false);
    setNeedsConsent(true);
    setConsentStatusReady(false);
    persistAuth(trimmed, null, false, false, 'hospital');
    return { ok: true, needsConsent: true };
  }, []);

  const logout = useCallback(() => {
    sttService.close?.();
    setIsAuthenticated(false);
    setMustChangePassword(false);
    setNeedsConsent(false);
    setConsentStatusReady(true);
    setAccountType('hospital');
    try {
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem(JWT_KEY);
      localStorage.removeItem(MCP_KEY);
      localStorage.removeItem(TERMS_KEY);
      localStorage.removeItem(ACCOUNT_TYPE_KEY);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setConsentStatusReady(true);
      setNeedsConsent(false);
      return undefined;
    }

    if (accountType === 'doctor') {
      setConsentStatusReady(true);
      setNeedsConsent(false);
      return undefined;
    }

    let cancelled = false;
    setConsentStatusReady(false);

    (async () => {
      try {
        const jwt = typeof window !== 'undefined' ? localStorage.getItem(JWT_KEY) : '';
        const me = jwt
          ? await apiGet('/api/nurse/me')
          : await apiGet(`/api/nurse/me?name=${encodeURIComponent(nurseName)}`);
        if (cancelled) return;
        setMustChangePassword(Boolean(me.must_change_password));
        writeFlag(MCP_KEY, Boolean(me.must_change_password));
        applyConsentAccepted(me.terms_accepted);
      } catch (err) {
        if (cancelled) return;
        const jwt = typeof window !== 'undefined' ? localStorage.getItem(JWT_KEY) : '';
        if (jwt && (err?.status === 401 || err?.status === 403)) {
          logout();
          return;
        }
        if (!readFlag(TERMS_KEY)) {
          setNeedsConsent(true);
        }
      } finally {
        if (!cancelled) setConsentStatusReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountType, applyConsentAccepted, isAuthenticated, logout, nurseName]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      nurseName,
      accountType,
      login,
      loginWithToken,
      logout,
      mustChangePassword,
      setMustChangePassword: (value) => {
        setMustChangePassword(value);
        writeFlag(MCP_KEY, value);
      },
      needsConsent,
      consentStatusReady,
      markConsentAccepted: () => applyConsentAccepted(true),
    }),
    [
      isAuthenticated,
      nurseName,
      accountType,
      login,
      loginWithToken,
      logout,
      mustChangePassword,
      needsConsent,
      consentStatusReady,
      applyConsentAccepted,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
